import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Phone, Mail, MapPin, X, Star, ChevronDown, ChevronRight, ChevronLeft, Check, Dumbbell, Target, Heart, Zap, Users, Clock, Award, ArrowRight, Play, Sparkles, Shield, TrendingUp, Activity, CheckCircle2, Calendar, ExternalLink, HelpCircle, MessageCircle, Home, Flame, Mountain, Scan, Apple, CircleDot, ParkingCircle, Globe, Monitor, Video, Instagram, Facebook, Linkedin, Gift, LogIn, Trophy, BarChart3, Link2, Share2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';
import { SEOHead } from '../hooks/usePageSEO';
import { LOGO_URL, STUDIO_IMAGE, LAURENT_IMAGE, TARAH_IMAGE, BOOKING_URL, EMS_SITE_URL, WHATSAPP_URL, SOCIAL_LINKS, IMAGES } from '../data/constants';
import { services } from '../data/services';
import { blogArticles } from '../data/blogData';
import { pricingCategories } from '../data/pricingData';
import { testimonials } from '../data/testimonials';
import { faqItems } from '../data/faqData';
import { AnimatedSection, BackNav, useIntersectionObserver, SectionBadge, AnimatedBadge, ParticlesBackground } from '../components/shared';
import { ServiceCard, ServiceCardWhite } from '../components/ServiceCards';
import { TestimonialsCarousel } from '../components/TestimonialsCarousel';
import { HomeContactSection } from '../components/HomeContactSection';
import { BlogArticleModal } from '../components/BlogArticleModal';
import { InternalLinks } from '../components/InternalLinks';

const BlogArticlePage = () => {
  const { articleId } = useParams();
  const { language } = useLanguage();
  const navigate = useNavigate();
  
  const article = blogArticles.find(a => a.id === articleId);

  if (!article) {
    return (
      <main className="pt-32 min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-4">
            {language === 'fr' ? 'Article non trouvé' : 'Article not found'}
          </h1>
          <Button onClick={() => navigate("/blog")} className="bg-primary hover:bg-primary/90">
            {language === 'fr' ? 'Retour au blog' : 'Back to blog'}
          </Button>
        </div>
      </main>
    );
  }

  return (
    <main id="main-content" role="main" className="pt-32" data-testid="blog-article-page">
      <SEOHead
        title={`${language === 'fr' ? article.title : article.titleEn} | Body-Up Nyon`}
        description={language === 'fr' ? article.excerpt : article.excerptEn}
        path={`/blog/${article.id}`}
        image={article.image}
        type="article"
      />
      <section className="relative py-20 bg-gradient-to-b from-black to-gray-900 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src={article.image} 
            alt={language === 'fr' ? (article.imageAlt || article.title) : (article.imageAltEn || article.titleEn)} 
            className="w-full h-full object-cover"
            loading="eager"
            fetchPriority="high"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/90 to-gray-900" />
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <AnimatedSection>
            <BackNav label={language === 'fr' ? 'Retour au blog' : 'Back to blog'} />
            
            <div className="flex items-center gap-4 mb-6">
              <span className="px-3 py-1 bg-primary/90 text-white text-sm font-medium rounded-full">
                {language === 'fr' ? article.category : article.categoryEn}
              </span>
              <span className="flex items-center gap-2 text-gray-400 text-sm">
                <Clock className="w-4 h-4" aria-hidden="true" />
                {article.readTime} {language === 'fr' ? 'de lecture' : 'read'}
              </span>
            </div>
            
            <div className="flex items-center gap-4 mb-6">
              <span className="text-5xl" role="img" aria-label={article.category}>{article.emoji}</span>
            </div>
            
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-6">
              {language === 'fr' ? article.title : article.titleEn}
            </h1>
            
            <p className="text-xl text-gray-300">
              {language === 'fr' ? article.excerpt : article.excerptEn}
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="prose prose-invert max-w-none">
              <p className="text-lg text-gray-300 mb-10 leading-relaxed">
                {language === 'fr' ? article.content.intro : article.content.introEn}
              </p>
              
              {article.content.sections.map((section, index) => (
                <div key={index} className="mb-10">
                  <h2 className="text-2xl font-bold text-white mb-4">
                    {language === 'fr' ? section.title : section.titleEn}
                  </h2>
                  <div className="bg-gradient-to-r from-primary/10 to-transparent rounded-xl p-6 border-l-4 border-primary">
                    <p className="text-gray-300 leading-relaxed">
                      {language === 'fr' ? section.text : section.textEn}
                    </p>
                    {section.link && (
                      <a 
                        href={section.link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-primary hover:underline mt-4 text-sm"
                      >
                        {language === 'fr' ? '📎 Source scientifique' : '📎 Scientific source'}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </AnimatedSection>

          {/* Local CTA if available */}
          {article.content.localCTA && (
            <AnimatedSection delay={150}>
              <div className="mt-8 bg-gradient-to-r from-primary/20 to-pink-500/20 rounded-2xl p-6 border border-primary/30">
                <p className="text-center text-white font-semibold text-lg">
                  📍 {language === 'fr' ? article.content.localCTA : article.content.localCTAEn}
                </p>
              </div>
            </AnimatedSection>
          )}

          {/* Related Services */}
          {article.relatedServices && article.relatedServices.length > 0 && (
            <AnimatedSection delay={175}>
              <div className="mt-8">
                <h3 className="text-lg font-bold text-white mb-4">
                  {language === 'fr' ? '🏋️ Services associés' : '🏋️ Related services'}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {article.relatedServices.map((service) => (
                    <Link 
                      key={service} 
                      to="/services"
                      className="px-4 py-2 bg-primary/20 hover:bg-primary/30 border border-primary/40 rounded-full text-primary text-sm font-medium transition-colors"
                    >
                      {service}
                    </Link>
                  ))}
                </div>
              </div>
            </AnimatedSection>
          )}

          {/* SEO Keywords Display */}
          {article.seoKeywords && article.seoKeywords.length > 0 && (
            <AnimatedSection delay={180}>
              <div className="mt-6">
                <div className="flex flex-wrap gap-2">
                  {article.seoKeywords.slice(0, 6).map((keyword) => (
                    <span key={keyword} className="px-3 py-1 bg-white/5 text-gray-400 text-xs rounded-full">
                      #{keyword.replace(/\s+/g, '')}
                    </span>
                  ))}
                </div>
              </div>
            </AnimatedSection>
          )}

          {/* Internal Links - SEO Maillage */}
          <AnimatedSection delay={185}>
            <InternalLinks type="services" />
          </AnimatedSection>
          
          {/* CTA */}
          <AnimatedSection delay={200}>
            <div className="mt-16 bg-gradient-to-br from-primary/20 to-primary/5 rounded-3xl p-10 border border-primary/30 text-center">
              <h3 className="text-2xl font-bold text-white mb-4">
                {language === 'fr' 
                  ? "🚀 Prêt à passer à l'action ?" 
                  : "🚀 Ready to take action?"
                }
              </h3>
              <p className="text-gray-300 mb-6">
                {language === 'fr' 
                  ? "Nos coachs experts vous accompagnent pour atteindre vos objectifs."
                  : "Our expert coaches will help you achieve your goals."
                }
              </p>
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="bg-primary hover:bg-primary/90 text-white">
                  <Calendar className="w-5 h-5 mr-2" />
                  {language === 'fr' ? 'Réserver une consultation gratuite' : 'Book a free consultation'}
                </Button>
              </a>
            </div>
          </AnimatedSection>

          {/* Local SEO Section */}
          <AnimatedSection delay={250}>
            <div className="mt-12 bg-white/5 rounded-2xl p-8 border border-white/10">
              <h3 className="text-lg font-bold text-white mb-4">
                📍 {language === 'fr' ? 'Body-Up Personal Training - Nyon & La Côte' : 'Body-Up Personal Training - Nyon & La Côte'}
              </h3>
              <p className="text-gray-400 text-sm mb-4">
                {language === 'fr' 
                  ? "Studio privé de personal training situé à Nyon, Route des Tattes d'Oie 95. Nous accueillons des clients de toute la région :"
                  : "Private personal training studio located in Nyon, Route des Tattes d'Oie 95. We welcome clients from the entire region:"
                }
              </p>
              <div className="flex flex-wrap gap-2 mb-4">
                {['Nyon', 'Gland', 'Rolle', 'Coppet', 'Prangins', 'Founex', 'Céligny', 'Tannay', 'Mies', 'Versoix', 'Morges', 'Genève'].map((city) => (
                  <span key={city} className="px-3 py-1 bg-primary/10 text-primary/80 text-xs rounded-full font-medium">
                    {city}
                  </span>
                ))}
              </div>
              <p className="text-gray-500 text-xs">
                {language === 'fr' 
                  ? "Services : Personal Training • EMS Training • Duo Training • Musculation • Perte de Poids • Coaching Sportif • Plan Alimentaire • Scan 3D Styku"
                  : "Services: Personal Training • EMS Training • Duo Training • Weight Training • Weight Loss • Sports Coaching • Meal Plan • 3D Styku Scan"
                }
              </p>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </main>
  );
};

// Privacy Policy Page

export default BlogArticlePage;
